import { APIError, TSegmentTypes, User } from '../models'
import ServiceConfigService, {
  IServiceConfig,
} from '../services/ServiceConfigService'
import ShareLinkService from '../services/ShareLinkService'
import UserService from '../services/UserService'
import history from '../history'
import { includeCurrentLocaleToPath } from './localeHelpers'
import generateConsumer from '../channels/consumer'
import { Dispatch, SetStateAction } from 'react'
import * as Sentry from '@sentry/react'

/**
 * Check whether the current user visited onboarding.
 * If not, display it right now.
 * If the currentUser's onboarding flag is set to false, but
 * the localStorage says that he visited the onboarding,
 * do not display the onboarding. Instead, update the API.
 * @param currentUser
 */
async function checkOnboarding(currentUser: User) {
  if (
    history.location &&
    history.location.pathname &&
    history.location.pathname.startsWith('/sr')
  ) {
    return
  }
  if (
    history.location &&
    history.location.pathname &&
    history.location.pathname.startsWith('/surveys/')
  ) {
    return
  }
  if (localStorage.getItem('skip_onboarding') === 'true') {
    localStorage.removeItem('skip_onboarding')
    return
  }
  if (currentUser) {
    if (!currentUser.segment_type) {
      const onboardingSegmentLS = localStorage.getItem('onboarding_tos_segment')
      if (onboardingSegmentLS) {
        await UserService.saveSegment(onboardingSegmentLS as TSegmentTypes)
      }
    }
  }
}
/**
 * Check if user should be redirected somewhere after sign in.
 */
function checkRedirections(fallback?: string) {
  const noRedirect = localStorage.getItem('noRedirect')

  if (noRedirect) {
    localStorage.removeItem('after_login')
    localStorage.removeItem('noRedirect')
    return
  }

  const redirect = localStorage.getItem('after_login')
  if (redirect) {
    window.location.replace(redirect)
    localStorage.removeItem('after_login')
    return true
  } else if (fallback) {
    window.location.replace(fallback)
    return true
  }
  return false
}

async function getServiceConfig(setServiceConfig) {
  try {
    const response = await ServiceConfigService.getServiceConfig()
    if (response instanceof APIError) {
      console.error('Could not fetch service config')
    } else {
      setServiceConfig(response)
    }
  } catch (e) {
    console.error('Could not fetch service config', e)
  }
}

export async function initializeApp(
  setServiceConfig: Dispatch<SetStateAction<IServiceConfig>>,
  setInitPerformed: Dispatch<SetStateAction<boolean>>,
  setCurrentUser: Dispatch<SetStateAction<User>>,
): Promise<void> {
  try {
    await getServiceConfig(setServiceConfig)
    const user = await UserService.getAuthCurrentUser()

    if (user) {
      localStorage.setItem('loggedin', 'yes')

      await ShareLinkService.checkSharePrice()

      const response = await UserService.getUserData()

      if (response instanceof APIError) {
        UserService.signOut()
      } else {
        if (response) {
          // Identify in HelpScout Beacon
          if (window && window.Beacon && response) {
            window.Beacon('identify', {
              name: `${response.first_name}` || 'New user',
              email: response.email,
            })
          }

          Sentry.setUser({ email: response.email })

          const anyRedirection = checkRedirections()
          if (!anyRedirection) {
            await checkOnboarding(response)
          }

          UserService.updateLastAccess()
        }

        setCurrentUser(response)
      }
    } else {
      localStorage.removeItem('loggedin')
    }

    return setInitPerformed(true)
  } catch (e) {
    console.log(e)
    setInitPerformed(true)
    // UserService.signOut()
  }
}
